import { MessageMarkdown } from '@/components/ui/messages/message-markdown';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BlueprintReadme,
  fetchBlueprintDetailByPath,
  getReadme,
  useBlueprintStore,
  getFileOnGit,
} from '@/store/blueprint';
import { useMutation, useQuery } from '@tanstack/react-query';
import path from 'path';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BoxIcon, SendIcon, MessageSquareIcon, CheckCircle2Icon, SparklesIcon, Loader2, StopCircle } from 'lucide-react';
import {
  createDeploy,
  createProject,
  getServerlessInstanceDefault,
  startProject,
  getServerlessEndpointById,
  runParabricksSSE,
  cancelTask,
} from '@/service/serverless-gpu';
import {
  parseSSEStream,
  SSETaskIdData,
  SSEStatusData,
  SSEOutputData,
  SSECompleteData,
  SSEErrorData,
} from '@/utils/sse';
import { useGetServerlessInstanceDefault, useGetServerlessProjects, useUploadFileOnRemote, useGetServerlessStorage, useRemoveFileOnRemote, useDownloadFileOnRemote } from '@/service/serverless-gpu/hook';
import { useServerlessStore } from '@/store/serverless';
import PATH_PAGE from '@/router/path';
import { RouteParams } from '@/router/type';
import { toastSomethingError } from '@/components/ui/toast-content';
import { join } from 'path-browserify';
import { toBase64 } from '@/utils';
import axios from 'axios';
import { Upload, FileText, Image as ImageIcon, File } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.mjs?url';

// Configure PDF.js worker to use local module
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Map blueprint names to model IDs
const BLUEPRINT_MODEL_MAP: Record<string, string> = {
  'Qwen3 30B A3B': 'qwen3-a3b',
  'Gemma3-27b': 'gemma3-27b',
  'Typhoon2.1-gemma3-12b': 'typhoon-gemma3',
  'Thai Document OCR': 'typhoon-ocr-7b',
};

const ServerlessBlueprintById = () => {
  const { workspaceId } = useParams<RouteParams>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const name = searchParams.get('name') || '';
  const { getByName } = useBlueprintStore();
  const blueprint = getByName(name);
  const bluePrintPath = blueprint?.bluePrintPath || '';
  const blueprintCategory = blueprint?.category || '';
  const modelId = BLUEPRINT_MODEL_MAP[name] || 'qwen3-a3b'; // Default to qwen3-a3b
  const [selectedAudience, setSelectedAudience] = useState<string>('user');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const [readme, setReadme] = useState<BlueprintReadme | null>(null);
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isAlreadyDeployed, setIsAlreadyDeployed] = useState(false);
  const [showDeployDialog, setShowDeployDialog] = useState(false);
  const [customProjectName, setCustomProjectName] = useState('');
  const [isOnboarding, setIsOnboarding] = useState(true);
  const [onboardingProgress, setOnboardingProgress] = useState(0);
  const [onboardingStep, setOnboardingStep] = useState<'checking' | 'deploying' | 'waiting' | 'complete'>('checking');
  const [endpointUrl, setEndpointUrl] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);

  // OCR-specific state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const [ocrResult, setOcrResult] = useState<string | null>(null);
  const [nerResult, setNerResult] = useState<string | null>(null);
  const [classificationResult, setClassificationResult] = useState<string | null>(null);
  const [showMarkdown, setShowMarkdown] = useState(true);

  // Parabricks-specific state
  const [fastqFile1Path, setFastqFile1Path] = useState<string | null>(null);
  const [fastqFile2Path, setFastqFile2Path] = useState<string | null>(null);
  const [referenceGenomePath, setReferenceGenomePath] = useState<string | null>(null);
  const [uploadingFile, setUploadingFile] = useState<{ name: string; progress: number } | null>(null);
  const [outputFileName, setOutputFileName] = useState<string>('output.bam');
  const [isProcessingParabricks, setIsProcessingParabricks] = useState(false);
  const [parabricksStatus, setParabricksStatus] = useState<string>('');
  const [parabricksProgress, setParabricksProgress] = useState(0);
  const [parabricksOutputFiles, setParabricksOutputFiles] = useState<string[]>([]);
  const [parabricksProjectId, setParabricksProjectId] = useState<string | null>(null);
  const [isCreatingParabricksProject, setIsCreatingParabricksProject] = useState(false);
  const [selectingInputType, setSelectingInputType] = useState<'fastq1' | 'fastq2' | 'reference' | null>(null);
  // Parabricks SSE state
  const [parabricksTaskId, setParabricksTaskId] = useState<string | null>(null);
  const [parabricksLogs, setParabricksLogs] = useState<string[]>([]);
  const [isCancellingParabricks, setIsCancellingParabricks] = useState(false);
  const [parabricksDuration, setParabricksDuration] = useState<number | null>(null);
  const [parabricksError, setParabricksError] = useState<string | null>(null);

  // Parabricks hooks
  const { mutateAsync: uploadFileToStorage } = useUploadFileOnRemote();
  const { mutateAsync: removeFile } = useRemoveFileOnRemote();
  const { mutateAsync: downloadFile } = useDownloadFileOnRemote();
  const { data: storageData, refetch: refetchStorage, isLoading: isLoadingStorage } = useGetServerlessStorage(
    { project_id: parabricksProjectId || '' },
    { enabled: !!parabricksProjectId }
  );

  // Get user's serverless projects
  const { token } = useServerlessStore();
  const { data: projectsData } = useGetServerlessProjects({
    enabled: !!token,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['blueprint-detail', bluePrintPath],
    queryFn: () => fetchBlueprintDetailByPath(bluePrintPath),
    staleTime: 1000 * 60 * 60,
    enabled: !!bluePrintPath,
  });

  // Get available audiences and languages from readme data
  const audiences = data?.readme
    ? [...new Set(data.readme.map((r) => r.audience))]
    : [];
  const languages = data?.readme
    ? [...new Set(data.readme.map((r) => r.language))]
    : [];

  // Find readme based on selected audience and language
  useEffect(() => {
    if (data?.readme) {
      const foundReadme = data.readme.find(
        (r) =>
          r.audience === selectedAudience && r.language === selectedLanguage
      );
      setReadme(foundReadme || data.readme[0]);
    }
  }, [data, selectedAudience, selectedLanguage]);

  // Check if blueprint is already deployed (only active projects) - for Text category
  useEffect(() => {
    if (blueprintCategory !== 'Text' || !projectsData?.projects) return;

    const blueprintProjectPattern = `[Blueprint][${blueprintCategory}] ${name}`;
    const deployedProject = projectsData.projects.find(
      (project) => project.project_name === blueprintProjectPattern && project.status === 'active'
    );

    const deployed = !!deployedProject;
    setIsAlreadyDeployed(deployed);

    // If already deployed, fetch the endpoint information
    if (deployed && deployedProject) {
      getServerlessEndpointById({ project_id: deployedProject.project_id })
        .then((endpointData) => {
          // Construct the full endpoint URL with OpenAI compatibility path
          const fullUrl = `https://api.float16.cloud/task/run/function/${endpointData.endpoint}/chat/completions`;
          setEndpointUrl(fullUrl);
          setApiKey(endpointData.api_key);
        })
        .catch((error) => {
          console.error('Error fetching endpoint:', error);
        });
    }

    // Set initial welcome message
    const welcomeMessage = deployed
      ? `Hello! I'm ${name}. Your blueprint is already deployed. Ask me anything or try out my capabilities!`
      : `Hello! I'm ${name}. Your blueprint has been deployed successfully! Ask me anything or try out my capabilities.`;

    setChatMessages([{
      role: 'assistant',
      content: welcomeMessage
    }]);

    // If already deployed, skip onboarding
    if (deployed) {
      setOnboardingStep('complete');
      setOnboardingProgress(100);
      setIsOnboarding(false);
    } else {
      // Start onboarding process for first-time deployment
      setOnboardingStep('checking');
      setIsOnboarding(true);
    }
  }, [blueprintCategory, projectsData, name]);

  // Check if blueprint is already deployed - for OCR category
  useEffect(() => {
    if (blueprintCategory !== 'OCR' || !projectsData?.projects) return;

    const blueprintProjectPattern = `[Blueprint][${blueprintCategory}] ${name}`;
    const deployedProject = projectsData.projects.find(
      (project) => project.project_name === blueprintProjectPattern && project.status === 'active'
    );

    const deployed = !!deployedProject;
    setIsAlreadyDeployed(deployed);

    // If already deployed, fetch the endpoint information
    if (deployed && deployedProject) {
      getServerlessEndpointById({ project_id: deployedProject.project_id })
        .then((endpointData) => {
          // Construct the full endpoint URL with OpenAI compatibility path
          const fullUrl = `https://api.float16.cloud/task/run/function/${endpointData.endpoint}/chat/completions`;
          setEndpointUrl(fullUrl);
          setApiKey(endpointData.api_key);
        })
        .catch((error) => {
          console.error('Error fetching endpoint:', error);
        });
    }
  }, [blueprintCategory, projectsData, name]);

  // Check if blueprint project exists and create if not - for Parabricks category
  useEffect(() => {
    if (blueprintCategory !== 'Parabricks' || !projectsData?.projects) return;

    const blueprintProjectPattern = `[Blueprint][${blueprintCategory}] ${name}`;
    const existingProject = projectsData.projects.find(
      (project) => project.project_name === blueprintProjectPattern && project.status === 'active'
    );

    if (existingProject) {
      setParabricksProjectId(existingProject.project_id);
    } else if (!isCreatingParabricksProject && !parabricksProjectId) {
      // Create new project for Parabricks
      setIsCreatingParabricksProject(true);
      (async () => {
        try {
          const { instance_type } = await getServerlessInstanceDefault();
          const { project_id } = await createProject({
            project_name: blueprintProjectPattern,
            instance_type: instance_type,
          });
          setParabricksProjectId(project_id);
        } catch (error) {
          console.error('Error creating Parabricks project:', error);
          toastSomethingError(error);
        } finally {
          setIsCreatingParabricksProject(false);
        }
      })();
    }
  }, [blueprintCategory, projectsData, name, isCreatingParabricksProject]);

  // Onboarding timer and deployment flow
  useEffect(() => {
    if (blueprintCategory !== 'Text' || !isOnboarding || isAlreadyDeployed) return;

    if (onboardingStep === 'checking') {
      // Step 1: Checking (1 second)
      const duration = 1000;
      const interval = 50;
      const steps = duration / interval;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        const progress = (currentStep / steps) * 20; // 0-20%
        setOnboardingProgress(progress);

        if (currentStep >= steps) {
          setOnboardingStep('deploying');
          clearInterval(timer);
        }
      }, interval);

      return () => clearInterval(timer);
    }

    if (onboardingStep === 'deploying') {
      // Step 2: Deploy the blueprint automatically
      setOnboardingProgress(20);
      deployBlueprintMutation.mutate();
    }

    if (onboardingStep === 'waiting') {
      // Step 3: Waiting after deployment (3 seconds)
      const duration = 3000;
      const interval = 50;
      const steps = duration / interval;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        const progress = 60 + (currentStep / steps) * 40; // 60-100%
        setOnboardingProgress(progress);

        if (currentStep >= steps) {
          setOnboardingStep('complete');
          setIsOnboarding(false);
          clearInterval(timer);
        }
      }, interval);

      return () => clearInterval(timer);
    }
  }, [blueprintCategory, isOnboarding, onboardingStep, isAlreadyDeployed]);

  // get readme content
  const dataReadme = useQuery({
    queryKey: ['blueprint-readme', bluePrintPath, readme?.fileName],
    queryFn: () =>
      getReadme(bluePrintPath, `${readme?.path}${readme?.fileName}` || ''),
    staleTime: 1000 * 60 * 60,
    enabled: !!bluePrintPath && !!readme?.fileName,
  });

  // Automatic deployment mutation for onboarding
  const deployBlueprintMutation = useMutation({
    mutationFn: async () => {
      const { instance_type } = await getServerlessInstanceDefault();
      const { project_id } = await createProject({
        project_name: `[Blueprint][${blueprintCategory}] ${name}`,
        instance_type: instance_type,
      });

      const deploymentFile = data?.deploymentFile;
      const fileList = data?.fileList || [];
      const files =
        deploymentFile && fileList.length > 0
          ? [
              fileList.find(
                (file) =>
                  file.fileName === deploymentFile.fileName &&
                  file.path === deploymentFile.path
              ),
              ...fileList.filter(
                (file) =>
                  !(
                    file.fileName === deploymentFile.fileName &&
                    file.path === deploymentFile.path
                  )
              ),
            ].filter(Boolean)
          : fileList;

      const filesWithContent = await Promise.all(
        files.map(async (file) => {
          const filePath = join(
            bluePrintPath,
            file?.path ?? '',
            file?.fileName ?? ''
          );
          const content = await getFileOnGit(filePath!);
          return {
            name: join(file?.path ?? '', file?.fileName ?? ''),
            content: toBase64(content) ?? '',
          };
        })
      );

      await createDeploy({
        project_id: project_id,
        files: filesWithContent,
        config: {},
        requirements_content: '',
      });

      return project_id;
    },
    onSuccess: async (project_id) => {
      // Fetch endpoint information
      try {
        const endpointData = await getServerlessEndpointById({ project_id });
        // Construct the full endpoint URL with OpenAI compatibility path
        const fullUrl = `https://api.float16.cloud/task/run/function/${endpointData.endpoint}/chat/completions`;
        setEndpointUrl(fullUrl);
        setApiKey(endpointData.api_key);
      } catch (error) {
        console.error('Error fetching endpoint:', error);
      }

      // Move to waiting step after successful deployment
      setOnboardingProgress(60);
      setOnboardingStep('waiting');
    },
    onError: (error) => {
      toastSomethingError(error);
      setIsOnboarding(false);
    },
  });

  // Manual deployment mutation (for Deploy Blueprint button)
  const { mutate: manualDeploy, isPending: isDeploying } = useMutation({
    mutationFn: async (projectName: string) => {
      const { instance_type } = await getServerlessInstanceDefault();
      const { project_id } = await createProject({
        project_name: projectName,
        instance_type: instance_type,
      });

      const deploymentFile = data?.deploymentFile;
      const fileList = data?.fileList || [];
      const files =
        deploymentFile && fileList.length > 0
          ? [
              fileList.find(
                (file) =>
                  file.fileName === deploymentFile.fileName &&
                  file.path === deploymentFile.path
              ),
              ...fileList.filter(
                (file) =>
                  !(
                    file.fileName === deploymentFile.fileName &&
                    file.path === deploymentFile.path
                  )
              ),
            ].filter(Boolean)
          : fileList;

      const filesWithContent = await Promise.all(
        files.map(async (file) => {
          const filePath = join(
            bluePrintPath,
            file?.path ?? '',
            file?.fileName ?? ''
          );
          const content = await getFileOnGit(filePath!);
          return {
            name: join(file?.path ?? '', file?.fileName ?? ''),
            content: toBase64(content) ?? '',
          };
        })
      );

      await createDeploy({
        project_id: project_id,
        files: filesWithContent,
        config: {},
        requirements_content: '',
      });

      navigate(
        PATH_PAGE.dashboard.serverlessGPU.byId(workspaceId!, project_id)
          .deploy
      );
    },
    onError: (error) => {
      toastSomethingError(error);
    },
  });

  const handleDeploy = () => {
    if (isAlreadyDeployed) {
      // Show dialog for custom project name
      setCustomProjectName('');
      setShowDeployDialog(true);
    } else {
      // Direct deployment with default name
      const defaultProjectName = `[Blueprint][${blueprintCategory}] ${name}`;
      manualDeploy(defaultProjectName);
    }
  };

  const handleConfirmDeploy = () => {
    if (!customProjectName.trim()) return;
    manualDeploy(customProjectName);
    setShowDeployDialog(false);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    // Add user message
    const userMessage = { role: 'user' as const, content: inputMessage };
    setChatMessages(prev => [...prev, userMessage]);
    const currentMessage = inputMessage;
    setInputMessage('');

    // If no endpoint available, show error
    if (!endpointUrl || !apiKey) {
      const errorResponse = {
        role: 'assistant' as const,
        content: `Sorry, the endpoint is not ready yet. Please wait for the deployment to complete or refresh the page.`
      };
      setChatMessages(prev => [...prev, errorResponse]);
      return;
    }

    // Add loading message
    const loadingMessage = {
      role: 'assistant' as const,
      content: 'Thinking...'
    };
    setChatMessages(prev => [...prev, loadingMessage]);

    try {
      // Check if blueprint has "LLM" tag
      const hasLLMTag = blueprint?.tags?.includes('LLM') || false;

      // Prepare messages in OpenAI format (including chat history)
      const messages = chatMessages
        .filter(msg => msg.content !== 'Thinking...') // Exclude loading messages
        .filter(msg => {
          // Exclude initial welcome message
          if (msg.role === 'assistant' &&
              (msg.content.includes('Your blueprint is already deployed') ||
               msg.content.includes('Your blueprint has been deployed successfully'))) {
            return false;
          }
          return true;
        })
        .map(msg => ({
          role: msg.role,
          content: msg.content
        }));

      // Add the new user message
      messages.push({
        role: 'user',
        content: currentMessage
      });

      // Prepare request body
      const requestBody: any = {
        model: modelId,
        messages: messages
      };

      // Add system prompt for LLM blueprints
      if (hasLLMTag) {
        requestBody.messages.unshift({
          role: 'system',
          content: 'You are a friendly and approachable assistant with a warm, conversational tone. You are male Thai-England person always use ผม as self calling. You based on Thailand and Fluent both Thai and English language.'
        });
      }

      // Log request details for debugging
      console.log('=== CLIENT-SIDE API Request Debug (OpenAI Format) ===');
      console.log('Full Endpoint URL:', endpointUrl);
      console.log('API Key:', apiKey);
      console.log('Request Body:', requestBody);
      console.log('=====================================================');

      // Make direct CLIENT-SIDE fetch request from browser to API endpoint
      const response = await fetch(endpointUrl!, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();

      console.log('=== CLIENT-SIDE API Response Debug ===');
      console.log('Response:', data);
      console.log('======================================');

      // Parse OpenAI format response
      let assistantMessage = '';
      if (data.choices && data.choices[0]?.text) {
        // Text completion format: choices[0].text
        assistantMessage = data.choices[0].text;
      } else if (data.choices && data.choices[0]?.message?.content) {
        // Chat completion format: choices[0].message.content
        assistantMessage = data.choices[0].message.content;
      } else if (data.message) {
        // Fallback: direct message field
        assistantMessage = data.message;
      } else if (data.response) {
        // Fallback: response field
        assistantMessage = data.response;
      } else {
        // Last resort: stringify the whole response
        assistantMessage = JSON.stringify(data);
      }

      // Replace loading message with actual response
      setChatMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = {
          role: 'assistant' as const,
          content: assistantMessage
        };
        return newMessages;
      });
    } catch (error: any) {
      console.error('=== CLIENT-SIDE API Error Debug ===');
      console.error('Error:', error);
      console.error('Error Message:', error.message);
      console.error('===================================');

      // Replace loading message with error
      setChatMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = {
          role: 'assistant' as const,
          content: `Error: ${error.message}\n\nThis is a direct client-side request using OpenAI-compatible format.`
        };
        return newMessages;
      });
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-8"></div>;
  }

  if (error) {
    return <div className="p-8 text-red-500">Error loading blueprint</div>;
  }

  // Convert PDF to image at 600 DPI
  const convertPdfToImage = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    // Get first page
    const page = await pdf.getPage(1);

    // Calculate scale for 600 DPI (PDF default is 72 DPI)
    const scale = 600 / 72; // 8.33x scale for 600 DPI
    const viewport = page.getViewport({ scale });

    // Create canvas
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not get canvas context');

    canvas.height = viewport.height;
    canvas.width = viewport.width;

    // Render PDF page to canvas
    await page.render({
      canvasContext: context,
      viewport: viewport,
    }).promise;

    // Convert canvas to base64 image
    return canvas.toDataURL('image/png');
  };

  // Handler for file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload an image (PNG, JPG, JPEG) or PDF file');
      return;
    }

    setUploadedFile(file);
    setOcrResult(null);
    setNerResult(null);
    setClassificationResult(null);

    // Create preview URL for images or convert PDF to image
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else if (file.type === 'application/pdf') {
      try {
        // Convert PDF to image for preview
        const imageDataUrl = await convertPdfToImage(file);
        setFilePreviewUrl(imageDataUrl);
      } catch (error) {
        console.error('Error converting PDF to image:', error);
        setFilePreviewUrl(null);
      }
    } else {
      setFilePreviewUrl(null);
    }
  };

  // Handler for OCR processing
  const handleProcessOCR = async () => {
    if (!uploadedFile) return;

    setIsProcessingOCR(true);
    setOcrResult(null);
    setNerResult(null);
    setClassificationResult(null);

    try {
      let base64Data: string;

      // Convert PDF to image at 600 DPI, or use image directly
      if (uploadedFile.type === 'application/pdf') {
        base64Data = await convertPdfToImage(uploadedFile);
      } else {
        // Convert image file to base64
        const reader = new FileReader();
        const promise = new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
        });
        reader.readAsDataURL(uploadedFile);
        base64Data = await promise;
      }

      // Helper function to make API call and handle SSE streaming
      const makeOCRRequest = async (promptText: string) => {
        const response = await fetch('https://float16-demo-5.float16.cloud/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model: 'Qwen3-30B-A3B-Instruct',
            messages: [
              {
                role: 'user',
                content: [
                  {
                    type: 'image_url',
                    image_url: {
                      url: base64Data,
                    },
                  },
                  {
                    type: 'text',
                    text: promptText,
                  },
                ],
              },
            ],
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        // Handle SSE streaming response
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let accumulatedContent = '';

        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) break;

            // Decode the chunk
            const chunk = decoder.decode(value, { stream: true });

            // Split by newlines to handle multiple SSE events in one chunk
            const lines = chunk.split('\n');

            for (const line of lines) {
              // SSE format: "data: {...}"
              if (line.startsWith('data: ')) {
                const jsonStr = line.substring(6); // Remove "data: " prefix

                // Skip [DONE] message
                if (jsonStr === '[DONE]') continue;

                try {
                  const data = JSON.parse(jsonStr);

                  // Extract delta content from SSE chunk
                  if (data.choices && data.choices[0]?.delta?.content) {
                    accumulatedContent += data.choices[0].delta.content;
                  }
                } catch (e) {
                  // Skip invalid JSON
                  console.warn('Failed to parse SSE chunk:', jsonStr);
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }

        return accumulatedContent || 'No content received';
      };

      // Make 3 requests in parallel
      const [ocrText, nerText, classificationType] = await Promise.all([
        makeOCRRequest('Extract all text from this document into Markdown language.'),
        makeOCRRequest('Do NER from this document. Format the output as a JSON Structure.'),
        makeOCRRequest('Classify this document type (e.g., invoice, receipt, contract, ID card, passport, business card, form, letter, etc.). Provide only the document type and a brief explanation.'),
      ]);

      setOcrResult(ocrText);
      setNerResult(nerText);
      setClassificationResult(classificationType);
      setIsProcessingOCR(false);
    } catch (error: any) {
      console.error('OCR Error:', error);
      setOcrResult(`Error: ${error.message}`);
      setNerResult(`Error: ${error.message}`);
      setClassificationResult(`Error: ${error.message}`);
      setIsProcessingOCR(false);
    }
  };

  // Parabricks file upload handler
  const handleParabricksUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !parabricksProjectId) return;

    setUploadingFile({ name: file.name, progress: 0 });

    try {
      const { uploads } = await uploadFileToStorage({
        project_id: parabricksProjectId,
        file_list: [{ file_name: file.name, file_path: 'parabricks' }],
      });

      if (uploads && uploads.length > 0) {
        await axios.put(uploads[0].url, file, {
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || file.size));
            setUploadingFile({ name: file.name, progress });
          },
        });
        await refetchStorage();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toastSomethingError(error);
    } finally {
      setUploadingFile(null);
    }
  };

  // Delete file from storage
  const handleDeleteFile = async (filePath: string) => {
    if (!parabricksProjectId) return;
    try {
      await removeFile({ project_id: parabricksProjectId, project_path: [filePath] });
      // Clear selection if deleted file was selected
      if (fastqFile1Path === filePath) setFastqFile1Path(null);
      if (fastqFile2Path === filePath) setFastqFile2Path(null);
      if (referenceGenomePath === filePath) setReferenceGenomePath(null);
      await refetchStorage();
    } catch (error) {
      toastSomethingError(error);
    }
  };

  // Select file for input
  const handleSelectFile = (filePath: string) => {
    if (!selectingInputType) return;

    if (selectingInputType === 'fastq1') setFastqFile1Path(filePath);
    else if (selectingInputType === 'fastq2') setFastqFile2Path(filePath);
    else if (selectingInputType === 'reference') setReferenceGenomePath(filePath);

    setSelectingInputType(null);
  };

  // Get file name from path
  const getFileName = (path: string) => path.split('/').pop() || path;

  // Download file from storage
  const handleDownloadFile = async (filePath: string) => {
    if (!parabricksProjectId) return;
    try {
      const { downloads } = await downloadFile({
        project_id: parabricksProjectId,
        file_path: [filePath],
      });
      if (downloads?.[0]?.url) {
        window.open(downloads[0].url, '_blank');
      }
    } catch (error) {
      toastSomethingError(error);
    }
  };

  // Parabricks processing handler with real SSE
  const handleProcessParabricks = async () => {
    // Validate all files are selected
    if (!fastqFile1Path || !fastqFile2Path || !referenceGenomePath || !parabricksProjectId || !token) {
      alert('Please select all required files before processing.');
      return;
    }

    // Reset state
    setIsProcessingParabricks(true);
    setParabricksProgress(0);
    setParabricksOutputFiles([]);
    setParabricksTaskId(null);
    setParabricksLogs([]);
    setParabricksStatus('Initializing...');
    setParabricksDuration(null);
    setParabricksError(null);

    try {
      // Build the payload with correct paths (prefix: /apps/)
      const pathPrefix = '/apps';
      const payload = {
        project_id: parabricksProjectId,
        command: 'fq2bam' as const,
        ref: `${pathPrefix}/${referenceGenomePath}`,
        in_fq: [`${pathPrefix}/${fastqFile1Path}`, `${pathPrefix}/${fastqFile2Path}`],
        out_bam: `${pathPrefix}/parabricks/${outputFileName}`,
        timeout: 3600,
      };

      // Make SSE request
      const response = await runParabricksSSE(payload, token);

      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();

      // Process SSE events
      for await (const event of parseSSEStream(reader)) {
        switch (event.type) {
          case 'task_id': {
            const data = event.data as SSETaskIdData;
            setParabricksTaskId(data.task_id);
            setParabricksStatus('Task created');
            break;
          }
          case 'status': {
            const data = event.data as SSEStatusData;
            setParabricksStatus(data.message);
            break;
          }
          case 'output': {
            const data = event.data as SSEOutputData;
            setParabricksLogs((prev) => [...prev, data.output]);
            break;
          }
          case 'complete': {
            const data = event.data as SSECompleteData;
            setParabricksStatus(data.finish_reason === 'success' ? 'Processing complete!' : `Completed: ${data.finish_reason}`);
            setParabricksDuration(data.duration);
            setParabricksProgress(100);
            // Set output files based on user input
            const baseName = outputFileName.replace(/\.bam$/i, '');
            setParabricksOutputFiles([
              `parabricks/${outputFileName}`,
              `parabricks/${baseName}.bam.bai`,
            ]);
            setIsProcessingParabricks(false);
            // Refresh storage to show new files
            await refetchStorage();
            break;
          }
          case 'error': {
            const data = event.data as SSEErrorData;
            setParabricksStatus('Error');
            setParabricksError(data.error || data.message || 'Unknown error');
            setIsProcessingParabricks(false);
            toastSomethingError(data.error || data.message);
            break;
          }
        }
      }

      // If we exit the loop without complete/error, mark as done
      if (isProcessingParabricks) {
        setIsProcessingParabricks(false);
      }
    } catch (err: any) {
      setParabricksStatus('Error');
      setParabricksError(err.message || 'Unknown error');
      setIsProcessingParabricks(false);
      toastSomethingError(err.message);
    }
  };

  // Cancel Parabricks task
  const handleCancelParabricks = async () => {
    if (!parabricksTaskId || !token) return;

    setIsCancellingParabricks(true);
    try {
      await cancelTask(parabricksTaskId, token, 'user_requested');
      setParabricksStatus('Cancelled by user');
    } catch (err: any) {
      toastSomethingError(err.message);
    } finally {
      setIsCancellingParabricks(false);
    }
  };

  // For Parabricks category, show genomics processing interface
  if (blueprintCategory === 'Parabricks') {
    // Show loading while creating project
    if (isCreatingParabricksProject || !parabricksProjectId) {
      return (
        <div className="container mx-auto p-6">
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center space-y-4">
              <div className="h-8 w-8 mx-auto animate-spin rounded-full border-4 border-primary border-t-transparent" />
              <p className="text-sm text-gray-500">Creating project...</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="container mx-auto space-y-6 p-6">
        {/* Banner Image */}
        <div className="relative h-48 w-full overflow-hidden rounded-lg">
          <img
            src="/image/blueprint/parabricks/parabricks-banner.jpeg"
            alt="Parabricks fq2bam"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <h1 className="text-2xl font-bold text-white">{name}</h1>
            <p className="text-sm text-white/80">{blueprint?.description}</p>
          </div>
        </div>

        {/* Project Badge */}
        <div className="flex justify-end">
          <Badge variant="secondary">Project: {parabricksProjectId.slice(0, 8)}...</Badge>
        </div>

        {/* How to Use Guide */}
        <div className="rounded-lg border bg-blue-50 p-4">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">How to use fq2bam</h3>
          <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
            <li>Upload your FASTQ files and reference genome to Storage</li>
            <li>Select the uploaded files for each input field</li>
            <li>Click "Process Data" to run the fq2bam pipeline</li>
            <li>Download the output BAM files when complete</li>
          </ol>
        </div>

        {/* Main Parabricks Interface */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Storage Files - FIRST */}
            <div className="rounded-lg border bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Storage Files</h2>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" startIcon={<Upload className="h-4 w-4" />} disabled={!!uploadingFile} onClick={() => document.getElementById('parabricks-upload')?.click()}>
                    {uploadingFile ? `${uploadingFile.progress}%` : 'Upload'}
                  </Button>
                  <input id="parabricks-upload" type="file" className="hidden" onChange={handleParabricksUpload} />
                  <Button size="sm" variant="outline" onClick={() => refetchStorage()} disabled={isLoadingStorage}>
                    Refresh
                  </Button>
                </div>
              </div>
              {isLoadingStorage ? (
                <p className="text-sm text-gray-500">Loading...</p>
              ) : storageData?.files?.length ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {storageData.files.filter(f => f.path_name.startsWith('parabricks/')).map((file) => (
                    <div key={file.path_name} className={`flex items-center justify-between rounded-lg border p-2 ${selectingInputType ? 'cursor-pointer hover:bg-primary/5' : ''}`} onClick={() => selectingInputType && handleSelectFile(file.path_name)}>
                      <div className="flex items-center gap-2 truncate">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span className="text-sm truncate">{getFileName(file.path_name)}</span>
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={(e) => { e.stopPropagation(); handleDeleteFile(file.path_name); }}>
                        Delete
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Upload className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No files in storage</p>
                  <p className="text-xs">Upload your FASTQ and reference genome files to get started</p>
                </div>
              )}
            </div>

            {/* Input Files - SECOND */}
            <div className="rounded-lg border bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">Select Input Files</h2>
              {!(storageData?.files?.filter(f => f.path_name.startsWith('parabricks/')).length) && (
                <p className="text-sm text-amber-600 mb-4">Please upload files to storage first before selecting inputs.</p>
              )}

              {/* FASTQ File 1 */}
              <div className="mb-3 flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">FASTQ 1:</span>
                  {fastqFile1Path ? (
                    <span className="text-sm text-green-600">{getFileName(fastqFile1Path)}</span>
                  ) : (
                    <span className="text-sm text-gray-400">Not selected</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant={selectingInputType === 'fastq1' ? 'primary' : 'outline'} disabled={!storageData?.files?.filter(f => f.path_name.startsWith('parabricks/')).length} onClick={() => setSelectingInputType(selectingInputType === 'fastq1' ? null : 'fastq1')}>
                    {selectingInputType === 'fastq1' ? 'Click file above' : 'Select'}
                  </Button>
                  {fastqFile1Path && <Button size="sm" variant="ghost" onClick={() => setFastqFile1Path(null)}>Clear</Button>}
                </div>
              </div>

              {/* FASTQ File 2 */}
              <div className="mb-3 flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">FASTQ 2:</span>
                  {fastqFile2Path ? (
                    <span className="text-sm text-green-600">{getFileName(fastqFile2Path)}</span>
                  ) : (
                    <span className="text-sm text-gray-400">Not selected</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant={selectingInputType === 'fastq2' ? 'primary' : 'outline'} disabled={!storageData?.files?.filter(f => f.path_name.startsWith('parabricks/')).length} onClick={() => setSelectingInputType(selectingInputType === 'fastq2' ? null : 'fastq2')}>
                    {selectingInputType === 'fastq2' ? 'Click file above' : 'Select'}
                  </Button>
                  {fastqFile2Path && <Button size="sm" variant="ghost" onClick={() => setFastqFile2Path(null)}>Clear</Button>}
                </div>
              </div>

              {/* Reference Genome */}
              <div className="mb-4 flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Reference:</span>
                  {referenceGenomePath ? (
                    <span className="text-sm text-green-600">{getFileName(referenceGenomePath)}</span>
                  ) : (
                    <span className="text-sm text-gray-400">Not selected</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant={selectingInputType === 'reference' ? 'primary' : 'outline'} disabled={!storageData?.files?.filter(f => f.path_name.startsWith('parabricks/')).length} onClick={() => setSelectingInputType(selectingInputType === 'reference' ? null : 'reference')}>
                    {selectingInputType === 'reference' ? 'Click file above' : 'Select'}
                  </Button>
                  {referenceGenomePath && <Button size="sm" variant="ghost" onClick={() => setReferenceGenomePath(null)}>Clear</Button>}
                </div>
              </div>

              {/* Output File Name */}
              <div className="mb-4">
                <Label htmlFor="outputFileName">Output File Name</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">/parabricks/</span>
                  <Input
                    id="outputFileName"
                    value={outputFileName}
                    onChange={(e) => setOutputFileName(e.target.value)}
                    placeholder="output.bam"
                    className="flex-1"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Output: /parabricks/{outputFileName}, /parabricks/{outputFileName.replace(/\.bam$/i, '')}.bam.bai</p>
              </div>

              {/* Process and Cancel Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={handleProcessParabricks}
                  disabled={!fastqFile1Path || !fastqFile2Path || !referenceGenomePath || isProcessingParabricks}
                  className="flex-1"
                  variant="primary"
                  startIcon={isProcessingParabricks ? <Loader2 className="h-4 w-4 animate-spin" /> : undefined}
                >
                  {isProcessingParabricks ? 'Processing...' : 'Process Data (fq2bam)'}
                </Button>
                {isProcessingParabricks && parabricksTaskId && (
                  <Button
                    variant="destructive"
                    onClick={handleCancelParabricks}
                    disabled={isCancellingParabricks}
                    startIcon={isCancellingParabricks ? <Loader2 className="h-4 w-4 animate-spin" /> : <StopCircle className="h-4 w-4" />}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Output/Results */}
          <div className="space-y-6">
            <div className="rounded-lg border bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">Output / Results</h2>

              {/* Task Info */}
              {parabricksTaskId && (
                <div className="mb-4 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <span>Task ID: <code className="rounded bg-gray-100 px-1">{parabricksTaskId.slice(0, 8)}...</code></span>
                  {parabricksDuration !== null && (
                    <span>Duration: {parabricksDuration.toFixed(2)}s</span>
                  )}
                </div>
              )}

              {/* Processing Status */}
              {(isProcessingParabricks || parabricksStatus) && (
                <div className="mb-4 space-y-2">
                  <p className="text-sm font-medium">{parabricksStatus}</p>
                  {isProcessingParabricks && (
                    <>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                        <div
                          className="h-full rounded-full bg-primary transition-all duration-300"
                          style={{ width: `${parabricksProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500">{parabricksProgress}% Complete</p>
                    </>
                  )}
                </div>
              )}

              {/* Error Display */}
              {parabricksError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="text-sm font-medium text-red-800">Error</p>
                  <p className="text-sm text-red-600">{parabricksError}</p>
                </div>
              )}

              {/* Streaming Logs */}
              {parabricksLogs.length > 0 && (
                <div className="mb-4 space-y-2">
                  <p className="text-sm font-medium text-gray-700">Output Logs</p>
                  <pre className="max-h-[300px] overflow-auto rounded-lg bg-[#1e1e1e] p-4 font-mono text-sm text-white whitespace-pre-wrap">
                    {parabricksLogs.join('\n')}
                  </pre>
                </div>
              )}

              {/* Output Files */}
              {parabricksOutputFiles.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-green-600">Processing Complete!</p>
                  {parabricksOutputFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-primary" />
                        <span className="text-sm">{file}</span>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => handleDownloadFile(file)}>
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              ) : !isProcessingParabricks && !parabricksLogs.length && !parabricksError && (
                <div className="flex min-h-[200px] items-center justify-center text-center">
                  <div className="space-y-2">
                    <FileText className="mx-auto h-12 w-12 text-gray-300" />
                    <p className="text-sm text-gray-500">
                      Upload files and click "Process Data" to see results
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // For OCR category, show document processing interface
  if (blueprintCategory === 'OCR') {
    return (
      <div className="container mx-auto space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{name}</h1>
            <p className="text-sm text-gray-500">{blueprint?.description}</p>
          </div>
          <Button
            onClick={handleDeploy}
            variant={'primary'}
            startIcon={<BoxIcon size={16} />}
            disabled={isDeploying}
          >
            Deploy Blueprint
          </Button>
        </div>

        {/* Deploy Confirmation Dialog */}
        <Dialog open={showDeployDialog} onOpenChange={setShowDeployDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Deploy New Instance</DialogTitle>
              <DialogDescription>
                This blueprint is already deployed. Enter a custom project name for the new deployment.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="projectName">Project Name</Label>
                <Input
                  id="projectName"
                  value={customProjectName}
                  onChange={(e) => setCustomProjectName(e.target.value)}
                  placeholder="Enter project name..."
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirmDeploy()}
                />
                <p className="text-xs text-gray-500">
                  A unique name for this deployment
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDeployDialog(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmDeploy}
                disabled={!customProjectName.trim() || isDeploying}
                startIcon={<BoxIcon size={16} />}
              >
                {isDeploying ? 'Deploying...' : 'Deploy'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Main OCR Interface */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left Column: Upload Area */}
          <div className="space-y-6">
            <div className="rounded-lg border bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">Upload Document</h2>

              {/* File Upload Area */}
              <div className="space-y-4">
                <label
                  htmlFor="file-upload"
                  className="flex min-h-[200px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-primary hover:bg-primary/5"
                >
                  <div className="space-y-2 text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="text-sm text-gray-600">
                      <span className="font-semibold text-primary">Click to upload</span> or drag and drop
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, JPEG or PDF (max 10MB)
                    </p>
                  </div>
                  <input
                    id="file-upload"
                    type="file"
                    className="hidden"
                    accept="image/png,image/jpeg,image/jpg,application/pdf"
                    onChange={handleFileUpload}
                  />
                </label>

                {/* File Preview */}
                {uploadedFile && (
                  <div className="rounded-lg border bg-white p-4">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                        {uploadedFile.type.startsWith('image/') ? (
                          <ImageIcon className="h-6 w-6 text-primary" />
                        ) : (
                          <File className="h-6 w-6 text-primary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {uploadedFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(uploadedFile.size / 1024).toFixed(2)} KB
                          {uploadedFile.type === 'application/pdf' && ' • Converted to 600 DPI'}
                        </p>
                      </div>
                    </div>

                    {/* Image Preview */}
                    {filePreviewUrl && (
                      <div className="mt-4">
                        <img
                          src={filePreviewUrl}
                          alt="Preview"
                          className="max-h-[300px] w-full rounded-lg object-contain"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Process Button */}
                <Button
                  onClick={handleProcessOCR}
                  disabled={!uploadedFile || isProcessingOCR}
                  className="w-full"
                  variant="primary"
                >
                  {isProcessingOCR ? 'Processing...' : 'Extract Text'}
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column: Results */}
          <div className="space-y-6">
            <div className="rounded-lg border bg-white shadow-sm">
              <Tabs defaultValue="ocr" className="w-full">
                <TabsList variant={'outline'} className="w-full">
                  <TabsTrigger value="ocr" variant={'outline'} className="flex-1">
                    OCR Result
                  </TabsTrigger>
                  <TabsTrigger value="ner" variant={'outline'} className="flex-1">
                    Named Entities
                  </TabsTrigger>
                  <TabsTrigger value="classification" variant={'outline'} className="flex-1">
                    Document Type
                  </TabsTrigger>
                </TabsList>

                {/* OCR Tab */}
                <TabsContent value="ocr" className="p-6">
                  {ocrResult ? (
                    <div className="space-y-4">
                      {/* Toggle between Markdown and Raw Text */}
                      <div className="flex items-center justify-between">
                        <div className="flex gap-2">
                          <Button
                            onClick={() => setShowMarkdown(true)}
                            variant={showMarkdown ? 'primary' : 'outline'}
                            size="sm"
                          >
                            Markdown
                          </Button>
                          <Button
                            onClick={() => setShowMarkdown(false)}
                            variant={!showMarkdown ? 'primary' : 'outline'}
                            size="sm"
                          >
                            Raw Text
                          </Button>
                        </div>
                        <Button
                          onClick={() => navigator.clipboard.writeText(ocrResult)}
                          variant="outline"
                          size="sm"
                          startIcon={<FileText size={16} />}
                        >
                          Copy to Clipboard
                        </Button>
                      </div>

                      {/* Content Display */}
                      <div className="rounded-lg bg-gray-50 p-4">
                        {showMarkdown ? (
                          <MessageMarkdown content={ocrResult} />
                        ) : (
                          <pre className="whitespace-pre-wrap text-sm text-gray-900">
                            {ocrResult}
                          </pre>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex min-h-[300px] items-center justify-center text-center">
                      <div className="space-y-2">
                        <FileText className="mx-auto h-12 w-12 text-gray-300" />
                        <p className="text-sm text-gray-500">
                          {isProcessingOCR
                            ? 'Extracting text from document...'
                            : 'Upload and process a document to see extracted text'}
                        </p>
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* NER Tab */}
                <TabsContent value="ner" className="p-6">
                  {nerResult ? (
                    <div className="space-y-4">
                      {/* Toggle between Markdown and Raw Text */}
                      <div className="flex items-center justify-between">
                        <div className="flex gap-2">
                          <Button
                            onClick={() => setShowMarkdown(true)}
                            variant={showMarkdown ? 'primary' : 'outline'}
                            size="sm"
                          >
                            Markdown
                          </Button>
                          <Button
                            onClick={() => setShowMarkdown(false)}
                            variant={!showMarkdown ? 'primary' : 'outline'}
                            size="sm"
                          >
                            Raw Text
                          </Button>
                        </div>
                        <Button
                          onClick={() => navigator.clipboard.writeText(nerResult)}
                          variant="outline"
                          size="sm"
                          startIcon={<FileText size={16} />}
                        >
                          Copy to Clipboard
                        </Button>
                      </div>

                      {/* Content Display */}
                      <div className="rounded-lg bg-gray-50 p-4">
                        {showMarkdown ? (
                          <MessageMarkdown content={nerResult} />
                        ) : (
                          <pre className="whitespace-pre-wrap text-sm text-gray-900">
                            {nerResult}
                          </pre>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex min-h-[300px] items-center justify-center text-center">
                      <div className="space-y-2">
                        <FileText className="mx-auto h-12 w-12 text-gray-300" />
                        <p className="text-sm text-gray-500">
                          {isProcessingOCR
                            ? 'Identifying named entities...'
                            : 'Upload and process a document to see named entities'}
                        </p>
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Classification Tab */}
                <TabsContent value="classification" className="p-6">
                  {classificationResult ? (
                    <div className="space-y-4">
                      <div className="rounded-lg bg-gray-50 p-4">
                        <pre className="whitespace-pre-wrap text-sm text-gray-900">
                          {classificationResult}
                        </pre>
                      </div>
                      <Button
                        onClick={() => navigator.clipboard.writeText(classificationResult)}
                        variant="outline"
                        size="sm"
                        startIcon={<FileText size={16} />}
                      >
                        Copy to Clipboard
                      </Button>
                    </div>
                  ) : (
                    <div className="flex min-h-[300px] items-center justify-center text-center">
                      <div className="space-y-2">
                        <FileText className="mx-auto h-12 w-12 text-gray-300" />
                        <p className="text-sm text-gray-500">
                          {isProcessingOCR
                            ? 'Classifying document type...'
                            : 'Upload and process a document to see document type'}
                        </p>
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>

        {/* Documentation Section */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList variant={'outline'}>
            <TabsTrigger value="overview" variant={'outline'}>
              Overview
            </TabsTrigger>
            <TabsTrigger value="documentation" variant={'outline'}>
              Documentation
            </TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <div className="rounded-lg border bg-white p-6">
              <h3 className="mb-4 text-lg font-semibold">About this model</h3>
              <p className="text-sm text-gray-600 mb-4">{blueprint?.description}</p>
              {blueprint?.tags && (
                <div className="flex flex-wrap gap-2">
                  {blueprint.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="documentation" className="mt-4">
            <div className="rounded-lg border bg-white p-6">
              {dataReadme.isLoading ? (
                <div className="flex items-center justify-center p-8">
                  Loading documentation...
                </div>
              ) : dataReadme.error ? (
                <div className="text-red-500">Error loading documentation</div>
              ) : dataReadme.data ? (
                <MessageMarkdown content={dataReadme.data} />
              ) : (
                <div className="text-gray-500">No documentation available</div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // For Text category, show chat interface
  if (blueprintCategory === 'Text') {
    // Show onboarding screen for first-time deployment
    if (isOnboarding) {
      return (
        <div className="container mx-auto p-6">
          <div className="flex min-h-[600px] items-center justify-center">
            <div className="w-full max-w-md space-y-8 text-center">
              {/* Icon */}
              <div className="flex justify-center">
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/5">
                  <SparklesIcon className="h-12 w-12 text-primary animate-pulse" />
                </div>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <h2 className="text-3xl font-bold text-gray-900">
                  Preparing {name}
                </h2>
                <p className="text-gray-600">
                  Setting up your AI model environment...
                </p>
              </div>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-300 ease-out"
                    style={{ width: `${onboardingProgress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-500">
                  {Math.round(onboardingProgress)}% Complete
                </p>
              </div>

              {/* Loading Steps */}
              <div className="space-y-3 pt-4">
                <div className={`flex items-center gap-3 text-left ${onboardingProgress >= 20 ? 'opacity-100' : 'opacity-40'}`}>
                  <div className={`h-2 w-2 rounded-full ${onboardingProgress >= 20 ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <span className="text-sm text-gray-600">
                    {onboardingStep === 'checking' ? 'Checking deployment status...' : 'Deployment status checked'}
                  </span>
                </div>
                <div className={`flex items-center gap-3 text-left ${onboardingProgress >= 60 ? 'opacity-100' : 'opacity-40'}`}>
                  <div className={`h-2 w-2 rounded-full ${onboardingProgress >= 60 ? 'bg-green-500' : onboardingStep === 'deploying' ? 'bg-yellow-500 animate-pulse' : 'bg-gray-300'}`} />
                  <span className="text-sm text-gray-600">
                    {onboardingStep === 'deploying' ? 'Deploying blueprint...' : onboardingProgress >= 60 ? 'Blueprint deployed' : 'Deploy blueprint'}
                  </span>
                </div>
                <div className={`flex items-center gap-3 text-left ${onboardingProgress >= 100 ? 'opacity-100' : 'opacity-40'}`}>
                  <div className={`h-2 w-2 rounded-full ${onboardingProgress >= 100 ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <span className="text-sm text-gray-600">
                    {onboardingStep === 'waiting' ? 'Initializing chat interface...' : onboardingProgress >= 100 ? 'Ready to interact' : 'Initialize chat'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="container mx-auto space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{name}</h1>
            <p className="text-sm text-gray-500">{blueprint?.description}</p>
          </div>
          <Button
            onClick={handleDeploy}
            variant={'primary'}
            startIcon={<BoxIcon size={16} />}
            disabled={isDeploying}
          >
            Deploy Blueprint
          </Button>
        </div>

        {/* Deploy Confirmation Dialog */}
        <Dialog open={showDeployDialog} onOpenChange={setShowDeployDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Deploy New Instance</DialogTitle>
              <DialogDescription>
                This blueprint is already deployed. Enter a custom project name for the new deployment.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="projectName">Project Name</Label>
                <Input
                  id="projectName"
                  value={customProjectName}
                  onChange={(e) => setCustomProjectName(e.target.value)}
                  placeholder="Enter project name..."
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirmDeploy()}
                />
                <p className="text-xs text-gray-500">
                  A unique name for this deployment
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDeployDialog(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmDeploy}
                disabled={!customProjectName.trim() || isDeploying}
                startIcon={<BoxIcon size={16} />}
              >
                {isDeploying ? 'Deploying...' : 'Deploy'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Chat Interface */}
        <div className="rounded-lg border bg-white shadow-sm">
          {/* Chat Messages */}
          <div className="flex h-[600px] flex-col">
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {chatMessages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {message.role === 'assistant' && (
                        <MessageSquareIcon className="h-5 w-5 mt-0.5 shrink-0" />
                      )}
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Input Area */}
            <div className="border-t bg-gray-50 p-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type your message..."
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim()}
                  startIcon={<SendIcon size={16} />}
                >
                  Send
                </Button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {endpointUrl && apiKey
                  ? 'Connected to deployed model endpoint'
                  : 'Waiting for endpoint to be ready...'}
              </p>
            </div>
          </div>
        </div>

        {/* Documentation Tab */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList variant={'outline'}>
            <TabsTrigger value="overview" variant={'outline'}>
              Overview
            </TabsTrigger>
            <TabsTrigger value="documentation" variant={'outline'}>
              Documentation
            </TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <div className="rounded-lg border bg-white p-6">
              <h3 className="mb-4 text-lg font-semibold">About this model</h3>
              <p className="text-sm text-gray-600 mb-4">{blueprint?.description}</p>
              {blueprint?.tags && (
                <div className="flex flex-wrap gap-2">
                  {blueprint.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="documentation" className="mt-4">
            <div className="rounded-lg border bg-white p-6">
              {dataReadme.isLoading ? (
                <div className="flex items-center justify-center p-8">
                  Loading documentation...
                </div>
              ) : dataReadme.error ? (
                <div className="text-red-500">Error loading documentation</div>
              ) : dataReadme.data ? (
                <MessageMarkdown content={dataReadme.data} />
              ) : (
                <div className="text-gray-500">No documentation available</div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // For non-Text categories (like OCR), show original documentation view
  return (
    <div className="container mx-auto space-y-6 p-6">
      {/* Blueprint Name */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{name}</h1>
        <Button
          onClick={handleDeploy}
          variant={'primary'}
          startIcon={<BoxIcon size={16} />}
          disabled={isDeploying}
        >
          Deploy Blueprint
        </Button>
      </div>

      {/* Audience Tabs */}
      {audiences.length > 1 && (
        <div className="flex justify-between gap-2">
          <Tabs
            value={selectedAudience}
            onValueChange={setSelectedAudience}
            className="w-full"
          >
            <TabsList variant={'outline'}>
              {audiences.map((audience) => (
                <TabsTrigger
                  key={audience}
                  value={audience}
                  className="capitalize"
                  variant={'outline'}
                >
                  {audience}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {/* Language Selector */}
          {languages.length > 1 && (
            <div className="space-y-4">
              <Select
                value={selectedLanguage}
                onValueChange={setSelectedLanguage}
              >
                <SelectTrigger className="min-w-[70px]">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((language) => (
                    <SelectItem key={language} value={language}>
                      {language}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {/* Readme Content */}
      <div className="space-y-4">
        <div className="rounded-lg border bg-white p-6 dark:bg-gray-800">
          {dataReadme.isLoading ? (
            <div className="flex items-center justify-center p-8">
              Loading documentation...
            </div>
          ) : dataReadme.error ? (
            <div className="text-red-500">Error loading documentation</div>
          ) : dataReadme.data ? (
            <MessageMarkdown content={dataReadme.data} />
          ) : (
            <div className="text-gray-500">No documentation available</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServerlessBlueprintById;
